import React, { useState } from 'react';
import * as yup from 'yup';
import { Box, Grid, Tooltip, IconButton } from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import DeleteIcon from '@mui/icons-material/Delete';
import { Controller } from 'react-hook-form';
import { Dropdown, Button } from '@voicecare.ai/ui-library';
import OForm from '../common/OForm';
import { getDepartmentsByFacilityId } from '../../utils/apiClient';
import { isValidUSPhoneNumber } from '../../utils/helpers/format-phone-number';
import { useDetectMyRole } from '../../custom-hooks';
import { useTheme } from '../../ThemeContext';
import RoleInfoPopup from './RoleInfoPopup';
import useUserForm from '../../hooks/useUserForm';

const UserForm = ({ user, onClose, setRenderType, renderType, facilities, handleSubmit }) => {
  const { role } = useDetectMyRole();
  const { themeMode, currentTheme } = useTheme();
  const [showRoleInfoPopup, setShowRoleInfoPopup] = useState(false);
  
  const {
    initialFormValues,
    facilitiesDepartments,
    setFacilitiesDepartments,
    loadingStates,
    setLoadingStates,
    handleFormSubmit
  } = useUserForm(user, handleSubmit);
  
  const isAdmin = role === 'admin';
  const isSupervisor = role === 'supervisor';



  /**
   * Fetch departments for a specific facility
   */

  const fetchDepartments = async (facilityId) => {
    if (!facilityId || facilitiesDepartments[facilityId]) {
      return;
    }

    try {
      setLoadingStates(prev => ({ ...prev, [facilityId]: true }));
      
      const response = await getDepartmentsByFacilityId(facilityId);
      
      if (response.statusCode === 'SUCCESS' && Array.isArray(response.response)) {
        setFacilitiesDepartments(prev => ({ 
          ...prev, 
          [facilityId]: response.response 
        }));
      } else {
        setFacilitiesDepartments(prev => ({ 
          ...prev, 
          [facilityId]: [] 
        }));
      }
    } catch (error) {
      console.error('Error fetching departments:', error);
      setFacilitiesDepartments(prev => ({ 
        ...prev, 
        [facilityId]: [] 
      }));
    } finally {
      setLoadingStates(prev => ({ ...prev, [facilityId]: false }));
    }
  };

  // Define which roles current user can see/manage
  const getAllowedRoles = () => {
    if (isAdmin) {
      return ['admin', 'supervisor', 'user'];
    } else if (isSupervisor) {
      return ['supervisor', 'user'];
    }
    return [];
  };

  // Brief role overview for tooltip based on logged-in user
  const getRoleTooltipContent = () => {
    if (isAdmin) {
      return "Admin: Full organizational control\n   Manage all facilities, staff & enterprise settings\n\nSupervisor: Location management\n   Oversee staff at specific clinic/hospital location\n\nUser: Basic operations\n   Handle daily tasks within assigned facility\n\nClick on ⓘ to know more";
    } else if (isSupervisor) {
      return "Supervisor: Location management\n   Oversee staff at specific clinic/hospital location\n\nUser: Basic operations\n   Handle daily tasks within assigned facility\n\nClick on ⓘ to know more";
    }
    return "";
  };

  // Create different validation schemas for admin vs non-admin users
  const createValidationSchema = (currentRole) => {
    const baseValidation = {
      name: yup.string().required('Name is required'),
      email: yup
        .string()
        .required('Email is required')
        .email('Enter a valid email address'),
      phoneNumber: yup
        .string()
        .required('Phone No. is required')
        .test('valid-phone', 'Enter a valid US phone number', (value) => {
          return isValidUSPhoneNumber(value);
        }),
      role: yup.string().required('Role is required'),
    };

    // For admin users, completely exclude all facility/department related validations
    if (currentRole === 'admin') {
      return yup.object().shape(baseValidation);
    }

    // For non-admin users, add facility/department related validation
    const nonAdminValidation = {
      ...baseValidation,
      facilityIds: yup.array().optional(),
      departmentIds: yup.array().optional(),
    };

    // For non-admin users, add facility validation
    return yup.object().shape({
      ...nonAdminValidation,
      facilityDepartments: yup.array()
        .of(
          yup.object().shape({
            facilityId: yup.mixed().optional(),
            departmentIds: yup.array().optional(),
          })
        )
        .test(
          'has-facility-selection',
          'At least one facility must be selected',
          function(facilityDepartments) {
            return facilityDepartments?.some(fd => {
              if (Array.isArray(fd?.facilityId)) {
                return fd.facilityId.length > 0;
              }
              return fd?.facilityId;
            });
          }
        ),
    });
  };

  // Get validation schema for the form
  const getValidationSchema = () => {
    // For editing existing users, use their role
    if (user && user.role) {
      return createValidationSchema(user.role);
    }
    
    // For new users, use a flexible schema that doesn't require facility selection initially
    // This allows admin creation to work properly
    const baseValidation = {
      name: yup.string().required('Name is required'),
      email: yup
        .string()
        .required('Email is required')
        .email('Enter a valid email address'),
      phoneNumber: yup
        .string()
        .required('Phone No. is required')
        .test('valid-phone', 'Enter a valid US phone number', (value) => {
          return isValidUSPhoneNumber(value);
        }),
      role: yup.string().required('Role is required'),
      facilityIds: yup.array().optional(),
      departmentIds: yup.array().optional(),
    };

    return yup.object().shape({
      ...baseValidation,
      facilityDepartments: yup.array()
        .of(
          yup.object().shape({
            facilityId: yup.mixed().optional(),
            departmentIds: yup.array().optional(),
          })
        )
        .test(
          'conditional-facility-selection',
          'At least one facility must be selected',
          function(facilityDepartments) {
            const { role } = this.parent;
            
            // Admin users don't need facility selection
            if (role === 'admin') {
              return true;
            }
            
            // Supervisor and user roles need facility selection
            return facilityDepartments?.some(fd => {
              if (Array.isArray(fd?.facilityId)) {
                return fd.facilityId.length > 0;
              }
              return fd?.facilityId;
            });
          }
        ),
    });
  };


  const getRoleOptions = () => {
    const options = [];
    if (isAdmin) {
      options.push({ label: 'Admin', value: 'admin' });
      options.push({ label: 'Supervisor', value: 'supervisor' });
      options.push({ label: 'User', value: 'user' });
    } else if (isSupervisor) {
      options.push({ label: 'User', value: 'user' });
    }
    return options;
  };


  const getFields = (watch, { setValue, getValues }) => {
    const facilityDepartments = watch('facilityDepartments') || [];
    const currentRole = watch('role');
    
    // Dynamic section headers based on role
    const getSectionHeader = (role) => {
      switch(role) {
        case 'admin':
          return 'Administrative Access'; // Won't be shown anyway
        case 'supervisor':
          return 'Facility Management';
        case 'user':
        default:
          return 'Facility and Department Assignment';
      }
    };
    
    const baseFields = [
    {
      fieldName: 'name',
      label: 'Name*',
      placeholder: 'Name',
      renderType: 'text',
      isRequired: true,
      section: 'User Information',
    },
    {
      fieldName: 'email',
      label: 'Email*',
      placeholder: 'Email',
      renderType: 'text',
      isRequired: true,
      disabled: user ? true : false,
      section: 'User Information',
    },
    {
      fieldName: 'phoneNumber',
      label: 'Phone Number*',
      placeholder: 'Phone Number',
      renderType: 'phoneNumber',
      isRequired: true,
      section: 'User Information',
    },
    {
      fieldName: 'role',
      label: 'Role*',
      renderType: 'custom',
      isRequired: true,
      section: 'User Information',
      render: ({ hasError, fieldError, control }) => (
        <Grid container spacing={0} alignItems="center" sx={{ width: '100%' }}>
          <Grid item xs={1} md={10.8}>
            <Controller
              name="role"
              control={control}
              render={({ field, fieldState }) => (
                <Dropdown
                  label="Role*"
                  options={getRoleOptions()}
                  placeholder="Select Role"
                  value={
                    getRoleOptions().find((opt) => opt.value === field.value) ||
                    (field.value
                      ? { label: field.value, value: field.value }
                      : null)
                  }
                  onChange={(selected) => {
                    field.onChange(selected ? selected.value : "");
                  }}
                  themeMode={themeMode}
                  isSearchable={true}
                  disabled={!isAdmin && !isSupervisor}
                  width="98%"
                  error={!!fieldError || !!fieldState.error}
                  helperText={fieldError?.message || fieldState.error?.message}
                />
              )}
            />
          </Grid>
          <Grid item xs={1} md={1.2} sx={{ display: 'flex', justifyContent: 'flex-start',marginTop: '1rem' }}>
            {(isAdmin || isSupervisor) && (
              <Tooltip
                title={
                  <div style={{ whiteSpace: 'pre-line', lineHeight: 1.4 }}>
                    {getRoleTooltipContent().split('Click on ⓘ to know more')[0]}
                    <span style={{ 
                      fontWeight: '600',
                      display: 'block',
                      marginTop: '6px',
                      fontSize: '12px',
                      fontStyle: 'italic'
                    }}>
                      Click on ⓘ to know more
                    </span>
                  </div>
                }
                arrow
                placement="top"
                componentsProps={{
                  tooltip: {
                    sx: {
                      backgroundColor: `${themeMode === 'dark' ? '#3f3c57' : '#e2e2e2'} !important`,
                      color: `${themeMode === 'dark' ? '#ffffff' : '#000000'} !important`,
                      fontSize: '12px',
                      maxWidth: '350px',
                      padding: '12px 16px',
                      lineHeight: 1.4,
                      '& .MuiTooltip-arrow': {
                        color: `${themeMode === 'dark' ? '#3f3c57' : '#e2e2e2'} !important`,
                      },
                    },
                  },
                }}
              >
                <InfoOutlinedIcon
                  onClick={() => setShowRoleInfoPopup(true)}
                  sx={{ 
                    fontSize: 18, 
                    color: 'rgba(118, 118, 118, 0.7)',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                    '&:hover': {
                      color: 'rgba(118, 118, 118, 1)',
                      transform: 'scale(1.1)',
                    }
                  }}
                />
              </Tooltip>
            )}
          </Grid>
        </Grid>
      )
    },
      // Removed main facility dropdown - now handled in Department Assignment section
    ];

    // Department Selection Fields - Dynamic array like facility-form.js
    const departmentFields = facilityDepartments.map((facilityDept, index) => [{
      fieldName: `facilityDepartments.${index}.facilityId`,
      renderType: 'custom',
      label: currentRole === 'supervisor' ? 'Facilities' : 'Facility',
      section: getSectionHeader(currentRole),
      render: ({ control }) => (
        <Controller
          name={`facilityDepartments.${index}.facilityId`}
          control={control}
          render={({ field, fieldState }) => (
            <Dropdown
              label={currentRole === 'supervisor' ? 'Facilities' : 'Facility'}
              options={facilities
                .filter(facility => {
                  if (currentRole === 'supervisor') {
                    // For supervisors using multi-select, don't filter out already selected
                    return true;
                  }
                  // For users, exclude facilities already selected in other department selections
                  const otherSelectedFacilities = facilityDepartments
                    .filter((_, i) => i !== index)
                    .map(fd => fd.facilityId)
                    .filter(Boolean);
                  
                  return !otherSelectedFacilities.includes(facility._id);
                })
                .map(facility => ({
                  label: `${facility.facilityName} - (${facility.location.streetAddress})`,
                  value: facility._id,
                }))}
              placeholder={currentRole === 'supervisor' ? 'Select Facilities' : 'Select Facility'}
              type={currentRole === 'supervisor' ? 'multi-select' : 'single-select'}
              value={
                currentRole === 'supervisor' 
                  ? (Array.isArray(field.value) ? field.value.map(fId => {
                      const facility = facilities.find(f => f._id === fId);
                      return facility ? {
                        label: `${facility.facilityName} - (${facility.location.streetAddress})`,
                        value: facility._id
                      } : null;
                    }).filter(Boolean) : [])
                  : (field.value
                      ? facilities.find(f => f._id === field.value)
                        ? { 
                            label: `${facilities.find(f => f._id === field.value).facilityName} - (${facilities.find(f => f._id === field.value).location.streetAddress})`,
                            value: field.value 
                          }
                        : null
                      : null)
              }
              onChange={(selected) => {
                if (currentRole === 'supervisor') {
                  // Handle multi-select for supervisors
                  const selectedValues = Array.isArray(selected) ? selected.map(opt => opt.value) : [];
                  field.onChange(selectedValues);
                  
                  // Load departments for all selected facilities
                  selectedValues.forEach(facilityId => {
                    fetchDepartments(facilityId);
                  });
                } else {
                  // Handle single-select for users
                  const newValue = selected ? selected.value : '';
                  field.onChange(newValue);
                  
                  if (newValue) {
                    fetchDepartments(newValue);
                    setValue(`facilityDepartments.${index}.departmentIds`, []);
                  } else {
                    setValue(`facilityDepartments.${index}.departmentIds`, []);
                  }
                }
              }}
              themeMode={themeMode}
              isSearchable={true}
              width="98%"
              error={!!fieldState.error}
              helperText={fieldState.error?.message}
            />
          )}
        />
      ),
      hidden: (values) => values?.role === 'admin',
    },
    {
      fieldName: `facilityDepartments.${index}.departmentIds`,
      renderType: 'custom',
      label: 'Departments',
      section: getSectionHeader(currentRole),
      render: ({ control }) => (
        <Controller
          name={`facilityDepartments.${index}.departmentIds`}
          control={control}
          render={({ field, fieldState }) => {
            const facilityId = getValues(`facilityDepartments.${index}.facilityId`);
            const isLoading = loadingStates[facilityId] || false;
            const allDepartments = facilitiesDepartments[facilityId] || [];
            
            // Filter out inactive departments
            const departments = allDepartments.filter(dept => dept.isActive !== false);
            
            const departmentOptions = departments.map(dept => ({
              label: dept.name || 'Unknown Department',
              value: dept._id,
            }));
            
            let selectedValues = [];
            if (Array.isArray(field.value) && field.value.length > 0) {
              selectedValues = field.value
                .map(val => {
                  const foundDept = departments.find(dept => dept._id === val);
                  if (foundDept) {
                    return {
                      label: foundDept.name || 'Unknown Department',
                      value: foundDept._id
                    };
                  }
                  // Don't show inactive departments in selected values
                  return null;
                })
                .filter(Boolean);
            }
            
            return (
              <Dropdown
                label="Departments"
                options={departmentOptions}
                placeholder={
                  !facilityId 
                    ? 'Please select a facility first'
                    : isLoading 
                    ? 'Loading departments...'
                    : departments.length === 0
                    ? 'No departments available'
                    : 'Select Departments'
                }
                value={selectedValues}
                onChange={(selected) => {
                  const newValues = Array.isArray(selected) ? selected.map(opt => opt.value) : [];
                  field.onChange(newValues);
                }}
                themeMode={themeMode}
                type="multi-select"
                isSearchable={true}
                disabled={!facilityId || isLoading || departments.length === 0}
                width="98%"
                error={!!fieldState.error}
                helperText={
                  fieldState.error?.message ||
                  (!facilityId ? 'Please select a facility first' : 
                   departments.length === 0 && !isLoading ? 'No departments available for selected facility' : undefined)
                }
              />
            );
          }}
        />
      ),
      hidden: (values) => values?.role === 'admin' || values?.role === 'supervisor',
    },
    // Empty spacer to push actions to the end
    {
      fieldName: `facilityDept_spacer_${index}`,
      renderType: 'custom',
      section: getSectionHeader(currentRole),
      render: () => <Box sx={{ width: '100%' }} />,
      hidden: (values) => values?.role === 'admin' || values?.role === 'supervisor',
    },
    // Actions at the very end of the container
    {
      fieldName: `facilityDept_actions_${index}`,
      renderType: 'custom',
      section: getSectionHeader(currentRole),
      render: () => {
        const facilityId = getValues(`facilityDepartments.${index}.facilityId`);
        const departmentIds = getValues(`facilityDepartments.${index}.departmentIds`) || [];
        const hasValues = facilityId || departmentIds.length > 0;
        const canDelete = facilityDepartments.length > 1;
        const isLastItem = index === facilityDepartments.length - 1;
        
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', height: '100%', justifyContent: 'flex-end', mt: { xs: 1, md: 0 }, mr: 1.4 }}>
            {/* Add Another button - only on last item and only for users */}
            {isLastItem && currentRole === 'user' && (
              <Button
                size="small"
                label="Add Another Facility"
                style={{ borderRadius: '6px' }}
                onClick={() => {
                  const currentFacilityDepts = getValues('facilityDepartments') || [];
                  setValue('facilityDepartments', [...currentFacilityDepts, { facilityId: '', departmentIds: [] }]);
                }}
                sx={{ color: '#ffffff', background: currentTheme.loginPage.loginButtonColor, mr: (canDelete || hasValues) ? 1 : 0 }}
              />
            )}
            {/* Show delete button for multiple items OR clear button for single item with values */}
            {(canDelete || hasValues) && (
              <IconButton
                size="small"
                onClick={() => {
                  const currentFacilityDepts = getValues('facilityDepartments');
                  if (canDelete) {
                    // Remove the entire facility-department selection
                    setValue('facilityDepartments', currentFacilityDepts.filter((_, i) => i !== index));
                  } else {
                    // Clear the values but keep the structure
                    currentFacilityDepts[index] = { facilityId: '', departmentIds: [] };
                    setValue('facilityDepartments', [...currentFacilityDepts]);
                  }
                }}
                sx={{ color: '#FF4D4F' }}
                title={canDelete ? "Remove Facility Selection" : "Clear Selection"}
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            )}
          </Box>
        );
      },
      hidden: (values) => values?.role === 'admin' || values?.role === 'supervisor',
    }
    ]);

    return [
      ...baseFields,
      ...departmentFields.flat()
    ];
  };

  return (
    <Box>
      <OForm
        formTitle={user ? 'Edit User' : 'Add New User'}
        initialValues={initialFormValues}
        fields={getFields}
        validationSchema={getValidationSchema()}
        onSubmit={handleFormSubmit}
        handleBack={onClose}
        submitButtonText={user ? 'Update' : 'Create'}
        backButtonText="Cancel"
        fieldsPerRow={4}
        scrollableSections={['Facility and Department Assignment']}
        headerButtonLabel="Back"
        headerButtonOnClick={() => setRenderType(renderType === 'list' ? 'form' : 'list')}
      />

      {/* Role Information Popup */}
      {showRoleInfoPopup && (
        <RoleInfoPopup
          visible={showRoleInfoPopup}
          onClose={() => setShowRoleInfoPopup(false)}
          userRole={role}
          allowedRoles={getAllowedRoles()}
        />
      )}
    </Box>
  );
};

export default UserForm; 
